import { createContext, useState, useEffect } from 'react'
import { loginRequest, refreshTokenRequest } from '@services/authService'

export const AuthContext = createContext()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [token, setToken] = useState(null)
  const [refreshToken, setRefreshToken] = useState(null)
  const [forcePasswordChange, setForcePasswordChange] = useState(false)

  const persistSession = (userData, accessToken, refreshValue) => {
    setUser(userData)
    setToken(accessToken)
    setRefreshToken(refreshValue || null)
    setIsAuthenticated(true)

    // SEGURANÇA: Usar sessionStorage ao invés de localStorage para tokens
    // sessionStorage é limpo quando a aba/navegador fecha, reduzindo janela de exposição
    // localStorage persiste indefinidamente e é mais vulnerável a XSS
    sessionStorage.setItem('bravo-tax-user', JSON.stringify(userData))
    sessionStorage.setItem('bravo-tax-token', accessToken)
    if (refreshValue) {
      // Refresh token pode ficar em localStorage pois tem vida mais longa
      // Mas idealmente deveria ser HttpOnly cookie no backend
      localStorage.setItem('bravo-tax-refresh', refreshValue)
    } else {
      localStorage.removeItem('bravo-tax-refresh')
    }
  }

  const clearSession = () => {
    console.log('🔓 Limpando sessão...')
    // Limpa todo o estado de autenticação
    sessionStorage.removeItem('bravo-tax-user')
    sessionStorage.removeItem('bravo-tax-token')
    sessionStorage.removeItem('bravo-tax-force-password')
    localStorage.removeItem('bravo-tax-refresh')

    // Limpar também localStorage legacy (para migração)
    localStorage.removeItem('bravo-tax-user')
    localStorage.removeItem('bravo-tax-token')
    localStorage.removeItem('bravo-tax-force-password')

    setUser(null)
    setIsAuthenticated(false)
    setToken(null)
    setRefreshToken(null)
    setForcePasswordChange(false)
    console.log('✅ Sessão limpa. isAuthenticated:', false, 'forcePasswordChange:', false)
  }

  useEffect(() => {
    const initAuth = () => {
      // SEGURANÇA: Priorizar sessionStorage (mais seguro)
      let savedUser = sessionStorage.getItem('bravo-tax-user')
      let savedToken = sessionStorage.getItem('bravo-tax-token')
      let forcePassword = sessionStorage.getItem('bravo-tax-force-password')
      const savedRefresh = localStorage.getItem('bravo-tax-refresh')

      // Fallback para localStorage (migração de sessões antigas)
      if (!savedUser && !savedToken) {
        savedUser = localStorage.getItem('bravo-tax-user')
        savedToken = localStorage.getItem('bravo-tax-token')
        forcePassword = localStorage.getItem('bravo-tax-force-password')

        // Se encontrou em localStorage, migrar para sessionStorage e limpar localStorage
        if (savedUser && savedToken) {
          sessionStorage.setItem('bravo-tax-user', savedUser)
          sessionStorage.setItem('bravo-tax-token', savedToken)
          if (forcePassword) sessionStorage.setItem('bravo-tax-force-password', forcePassword)

          localStorage.removeItem('bravo-tax-user')
          localStorage.removeItem('bravo-tax-token')
          localStorage.removeItem('bravo-tax-force-password')
        }
      }

      // Limpa tokens mock antigos
      if (savedToken?.startsWith('mock-jwt-token')) {
        clearSession()
        setIsLoading(false)
        return
      }

      // Se tem usuário e token válidos, restaura a sessão
      if (savedUser && savedToken) {
        try {
          const userData = JSON.parse(savedUser)
          setUser(userData)
          setToken(savedToken)
          setRefreshToken(savedRefresh || null)
          setIsAuthenticated(true)

          // Só ativa o forcePasswordChange se estiver autenticado
          if (forcePassword === 'true') {
            setForcePasswordChange(true)
          }
        } catch (error) {
          console.error('Erro ao restaurar sessão:', error)
          clearSession()
        }
      }

      setIsLoading(false)
    }

    initAuth()

    // Listener para logout automático quando receber 401
    const handleUnauthorized = () => {
      logout()
      window.location.href = '/login'
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized)
  }, [])

  const login = async (username, password) => {
    try {
      setIsLoading(true)
      const authData = await loginRequest({ username, password })

      const profile = {
        username: authData.username || username,
        name: authData.username || username,
        email: username,
        user_id: authData.user_id
      }

      persistSession(profile, authData.access, authData.refresh)

      // Verifica se precisa forçar mudança de senha
      if (authData.force_password_change) {
        setForcePasswordChange(true)
        sessionStorage.setItem('bravo-tax-force-password', 'true')
      }

      return { success: true }
    } catch (error) {
      setIsAuthenticated(false)
      setUser(null)
      const raw = (error?.message || '').toLowerCase()
      const friendly = /nao autorizado|não autorizado|credenciais|invalid|no_active_account|401/.test(raw)
        ? 'Usuário/e-mail ou senha inválidos'
        : (error?.message || 'Falha ao autenticar')
      return { success: false, error: friendly }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    clearSession()
  }

  const clearForcePasswordChange = () => {
    setForcePasswordChange(false)
    sessionStorage.removeItem('bravo-tax-force-password')
    localStorage.removeItem('bravo-tax-force-password') // Limpeza legacy
  }

  const refreshSession = async () => {
    if (!refreshToken) return null
    try {
      const data = await refreshTokenRequest(refreshToken)
      if (data?.access) {
        persistSession(user || {}, data.access, refreshToken)
        return data.access
      }
    } catch (error) {
      logout()
    }
    return null
  }

  const updateUser = (userData) => {
    const updatedUser = { ...(user || {}), ...userData }
    setUser(updatedUser)
    sessionStorage.setItem('bravo-tax-user', JSON.stringify(updatedUser))
  }

  const value = {
    user,
    token,
    refreshToken,
    isAuthenticated,
    isLoading,
    forcePasswordChange,
    login,
    logout,
    refreshSession,
    updateUser,
    clearForcePasswordChange,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
